/**
 * Which destination an address belongs to, and whether a given link is it.
 *
 * **One function, because two navigations ask the same question** (A-17). It
 * lived inside `NavRail` as a private `activeKey` while `NavMenu` answered the
 * same question with `NavLink`'s `isActive` — and the two disagreed exactly
 * where it mattered: on `/admin/staff` the rail lit **People** through the view
 * map while the copy of People behind the phone bar's *More* lit nothing, for
 * the same account on the same screen (D-884).
 *
 * *The general rule this is an instance of: if the navigation and the router
 * both decide "may she go here", they will eventually disagree. The same holds
 * for "is she here".*
 */
import { ALL_DESTINATIONS } from './nav'
import { destinationForPath } from './navViews'

/**
 * Which destination this address belongs to (D-322).
 *
 * **`NavLink`'s own `isActive` cannot answer this**, and the audit caught it
 * marking nothing at all on the screen everybody lands on: `/` redirects per
 * account (D-176), so a parent sat on `/me` with no rail item lit, and People
 * went dark the moment the view bar took her to `/admin/staff`. A state you
 * cannot see is not fine (D-309).
 *
 * The views know which destination they belong to, so they answer it; a plain
 * prefix match covers the rest.
 *
 * **Which is why the rows are `Link` and not `NavLink`.** Nothing in either
 * navigation used a single `NavLink` feature that mattered — the class list is a
 * string and the current flag is this function's answer — and `NavLink` quietly
 * overrode the one that did: `aria-current = isActive ? ariaCurrentProp :
 * undefined`, so passing `aria-current="page"` did nothing unless *its* match
 * agreed. The highlight was painted from here and the accessible flag still came
 * from the thing this comment says cannot answer it, so on `/home`,
 * `/dashboard` and every view-mapped address a screen reader was told she was
 * nowhere (D-884).
 */
export function destinationAt(pathname: string): string | null {
  const byView = destinationForPath(pathname)
  if (byView) return byView

  /*
   * The two landing addresses. `/` redirects and `/home` is the composed screen
   * every signed-in account is sent to (D-384).
   *
   * `/me` and `/teacher` were in this list until they were deleted, and leaving
   * them here lit *Home* on their Page-not-found — a rail claiming she is
   * somewhere the app says does not exist. Small, but it is the same stale
   * reference that made those two routes rot in the first place.
   */
  /*
   * `/dashboard` is the third, and it carries no rail row of its own (D-878, D-884):
   * *What needs you* **is** her home, so the Home row is the one that lights
   * while she is on it. Without this she lands on her own home screen with
   * nothing in the rail marked — the exact fault the note above records.
   */
  if (pathname === '/' || pathname === '/home' || pathname === '/dashboard') return 'home'

  return destinationByPrefix(pathname)
}

/**
 * The destination whose address this path sits under, when no view claims it.
 *
 * `destinationForPath` reads the view rows, and a destination with no views —
 * People, Requests, All classes — is not in them. A record under one of those,
 * `/admin/classes/<id>`, belongs to the destination all the same.
 *
 * **Shared with `signInLanding`, which had its own identical copy** until an
 * audit of D-884 found it. The loop is one thing; the two *questions* are not,
 * and the difference is the landing clause above.
 *
 * **Folding that clause in would change nothing today** — `observed`: `home`
 * carries no capability, so `canReach(anyone, 'home')` is true and sign-in
 * reaches the same answer either way. *An earlier version of this comment
 * claimed the clause protected a parent from `/dashboard`. It does not, and
 * `GAPS.md` carries the gap it was describing.* They are kept apart because
 * highlighting a row and authorising an address are different questions that
 * happen to look alike — which is exactly why one will move and the other
 * will not.
 */
export function destinationByPrefix(pathname: string): string | null {
  /*
   * **The longest-wins tie-break is unreachable with today's destinations** and
   * is here for when it is not: no `to` is a prefix of another, so a path
   * matches at most one and `best` is assigned at most once. Any mutation of
   * the comparison survives, correctly — do not read that line as covered. The
   * nesting that actually needs resolving lives in `destinationForPath`.
   */
  let best: (typeof ALL_DESTINATIONS)[number] | null = null

  for (const d of ALL_DESTINATIONS) {
    if (d.to === '/') continue

    if (pathname === d.to || pathname.startsWith(d.to + '/')) {
      if (!best || d.to.length > best.to.length) best = d
    }
  }

  return best?.key ?? null
}

/**
 * The same answer for a caller holding an **address** rather than a key.
 *
 * `NavMenu`'s items are `{ label, to }`, so this looks the destination up by
 * its address and compares keys. **A `to` that names no destination falls back
 * to an exact match**, which is right rather than a loophole: the account
 * menu's only link is `/account`, which is a screen and not a destination, and
 * for an address with no views an exact match *is* the whole answer.
 */
export function marksCurrent(pathname: string, to: string): boolean {
  const named = ALL_DESTINATIONS.find((d) => d.to === to)?.key

  if (named === undefined) return pathname === to

  return destinationAt(pathname) === named
}
